import {
  getHttp1PseudoHeaderError,
  normalizeHttpHeaders,
  parseHttpTextToToolArgs,
  resolveEffectiveHttpVersion,
} from '@terra/shared-utils';

function resolveHttpParts(args: Record<string, unknown>): {
  method: string;
  headerObj: Record<string, string>;
  rawBody: unknown;
} {
  const options = args.options as Record<string, unknown> | undefined;
  const method =
    ((options?.method ?? args.method) as string | undefined)?.toUpperCase() ||
    'GET';
  const headerObj = ((options?.headers ?? args.headers) || {}) as Record<
    string,
    string
  >;
  const rawBody = options?.body ?? args.body ?? null;
  return { method, headerObj, rawBody };
}

function formatNormalizedHeaderLines({
  host,
  headerObj,
}: {
  host?: string;
  headerObj: Record<string, string>;
}): string[] {
  const mergedHeaders = host ? { Host: host, ...headerObj } : headerObj;
  const normalizedHeaders = normalizeHttpHeaders(mergedHeaders);

  return Object.entries(normalizedHeaders).flatMap(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map((singleValue) => `${key}: ${singleValue}`);
    }
    return [`${key}: ${value}`];
  });
}

export const formatHttpRequest = (
  args: Record<string, unknown>,
): { requestLine: string; headers: string[]; body: string | null } | null => {
  if (!args) return null;

  const url = args.url as string;
  if (!url) return null;

  let parsedUrl: URL;
  try {
    parsedUrl = new URL(url);
  } catch {
    return null;
  }

  const { method, headerObj, rawBody } = resolveHttpParts(args);

  const path = parsedUrl.pathname + parsedUrl.search;
  const requestLine = `${method} ${path} ${resolveEffectiveHttpVersion({
    httpVersion: args.httpVersion as string | undefined,
    headers: headerObj,
  })}`;

  const headers = formatNormalizedHeaderLines({
    host: parsedUrl.host,
    headerObj,
  }).map((headerLine) => {
    const separatorIndex = headerLine.indexOf(':');
    if (separatorIndex <= 0) {
      return headerLine;
    }

    const key = headerLine.slice(0, separatorIndex);
    const value = headerLine.slice(separatorIndex + 1).trim();
    const displayValue =
      key.toLowerCase() === 'authorization' &&
      value.toLowerCase().startsWith('bearer ')
        ? 'Bearer <token>'
        : value;
    return `${key}: ${displayValue}`;
  });

  let body: string | null = null;
  if (rawBody) {
    try {
      const parsed =
        typeof rawBody === 'string' ? JSON.parse(rawBody) : rawBody;
      body = JSON.stringify(parsed, null, 2);
    } catch {
      body = rawBody as string;
    }
  }

  return { requestLine, headers, body };
};

export function getMethodColorToken(method: string): string {
  switch (method.toUpperCase()) {
    case 'GET':
      return 'blue.9';
    case 'POST':
      return 'green.9';
    case 'PUT':
    case 'PATCH':
      return 'orange.9';
    case 'DELETE':
      return 'red.9';
    default:
      return 'purple.9';
  }
}

export function formatArgsToHttpText(args: Record<string, unknown>): string {
  const url = (args.url as string) || '';
  const { method, headerObj: headers, rawBody } = resolveHttpParts(args);
  const body = rawBody
    ? typeof rawBody === 'string'
      ? rawBody
      : JSON.stringify(rawBody, null, 2)
    : '';

  const lines: string[] = [];

  try {
    const parsedUrl = new URL(url);
    lines.push(
      `${method} ${parsedUrl.pathname}${parsedUrl.search} ${resolveEffectiveHttpVersion(
        {
          httpVersion: args.httpVersion as string | undefined,
          headers,
        },
      )}`,
    );
    lines.push(
      ...formatNormalizedHeaderLines({
        host: parsedUrl.host,
        headerObj: headers,
      }),
    );
  } catch {
    lines.push(
      `${method} ${url} ${resolveEffectiveHttpVersion({
        httpVersion: args.httpVersion as string | undefined,
        headers,
      })}`,
    );
    lines.push(...formatNormalizedHeaderLines({ headerObj: headers }));
  }

  if (body) {
    lines.push('', body);
  }

  return lines.join('\n');
}

export function parseHttpTextToArgs(
  text: string,
  originalUrl: string,
  originalCredentialId?: string,
): Record<string, unknown> | null {
  const pseudoHeaderError = getHttp1PseudoHeaderError({ requestText: text });
  if (pseudoHeaderError) {
    return null;
  }

  return parseHttpTextToToolArgs({
    text,
    originalUrl,
    originalCredentialId,
  });
}

export const formatValueForMarkdown = (value: unknown): string => {
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object' && value !== null) {
    return JSON.stringify(value, null, 2);
  }
  return String(value);
};

export const formatLabel = (key: string): string => {
  return key
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const getActionRequestMarkdown = (
  args?: Record<string, unknown>,
): string | null => {
  if (!args || Object.keys(args).length === 0) {
    return null;
  }

  const entries = Object.entries(args);
  const markdownLines = entries.map(([key, value]) => {
    const label = formatLabel(key);
    const formattedValue = formatValueForMarkdown(value);

    if (
      typeof value === 'object' ||
      (typeof formattedValue === 'string' && formattedValue.length > 100)
    ) {
      return `**${label}:**\n\`\`\`\n${formattedValue}\n\`\`\``;
    }

    return `**${label}:** ${formattedValue}`;
  });

  return markdownLines.join('\n\n');
};
